import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { ALL_REGIONS, REGIONS } from "../types";
import { SECURITY_CHECKS } from "./ScorePanel";

interface ProbeInputProps {
  onSubmit: (params: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body: string;
    regions: string[];
  }) => void;
  isLoading: boolean;
  isPro?: boolean;
  enabledChecks: Set<string>;
  onToggleCheck: (id: string) => void;
}

export default function ProbeInput({ onSubmit, isLoading, isPro = false, enabledChecks, onToggleCheck }: ProbeInputProps) {
  const { isSignedIn } = useAuth();
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState("GET");
  const [selectedRegions, setSelectedRegions] = useState<string[]>(ALL_REGIONS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showScoringConfig, setShowScoringConfig] = useState(false);
  const [headersRaw, setHeadersRaw] = useState("");
  const [body, setBody] = useState("")
  const [urlError, setUrlError] = useState("");

  function validateUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        setUrlError("URL must use http or https");
        return false;
      }
      setUrlError("");
      return true;
    } catch {
      setUrlError("Please enter a valid URL");
      return false;
    }
  }

  function parseHeaders(raw: string): Record<string, string> {
    const headers: Record<string, string> = {};
    for (const line of raw.split("\n")) {
      const idx = line.indexOf(":");
      if (idx === -1) continue;
      const key = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (key) headers[key] = value;
    }
    return headers;
  }

  function toggleRegion(region: string) {
    setSelectedRegions((prev) =>
      prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateUrl(url)) return;
    if (selectedRegions.length === 0) return;

    onSubmit({
      url,
      method,
      headers: isPro ? parseHeaders(headersRaw) : {},
      body: isPro && method === "POST" ? body : "",
      regions: selectedRegions,
    });
  }

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {isPro && (
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="input-dark"
            style={{ width: "auto", flex: "0 0 auto", padding: "12px 10px" }}
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
        )}
        <input
          type="text"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (urlError) validateUrl(e.target.value);
          }}
          placeholder="https://api.example.com/health"
          required
          className="input-dark"
          style={{
            flex: 1,
            fontSize: 16,
            padding: "12px 16px",
            borderColor: urlError ? "#FF5C5C" : undefined,
          }}
        />
        <button
          type="submit"
          disabled={isLoading || selectedRegions.length === 0}
          className="btn-run"
          style={{ padding: "12px 24px", fontSize: 15 }}
        >
          {isLoading ? "Running…" : "Run Test"}
        </button>
      </div>

      {urlError && (
        <p style={{ color: "#FF5C5C", fontSize: 13, margin: "0 0 8px" }}>{urlError}</p>
      )}

      {/* Region selector */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {ALL_REGIONS.map((r) => {
          const active = selectedRegions.includes(r);
          return (
            <button
              key={r}
              type="button"
              onClick={() => toggleRegion(r)}
              className={`region-chip${active ? " region-chip--active" : ""}`}
            >
              {REGIONS[r]}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() =>
            setSelectedRegions(selectedRegions.length === ALL_REGIONS.length ? [] : ALL_REGIONS)
          }
          style={{ fontSize: 12, color: "#98A1AB", background: "none", border: "none", cursor: "pointer" }}
        >
          {selectedRegions.length === ALL_REGIONS.length ? "Deselect all" : "Select all"}
        </button>
      </div>

      {/* Security scoring config */}
      <div style={{ marginBottom: isPro ? 4 : 0, textAlign: "center" }}>
        <button
          type="button"
          onClick={() => setShowScoringConfig((v) => !v)}
          style={{
            fontSize: 13,
            color: "#98A1AB",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginBottom: showScoringConfig ? 8 : 0,
            padding: 0,
          }}
        >
          {showScoringConfig ? "▼" : "▶"} Security scoring ({enabledChecks.size} of {SECURITY_CHECKS.length} checks enabled)
        </button>
        {showScoringConfig && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {SECURITY_CHECKS.map((check) => {
              const enabled = enabledChecks.has(check.id);
              return (
                <label
                  key={check.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 10px",
                    borderRadius: 6,
                    background: "rgba(255,255,255,0.03)",
                    cursor: "pointer",
                    opacity: enabled ? 1 : 0.5,
                    transition: "opacity 0.15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => onToggleCheck(check.id)}
                    style={{ accentColor: "#FF2566", width: 13, height: 13, flexShrink: 0, cursor: "pointer" }}
                  />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: "#EFF2F5", fontWeight: 600 }}>{check.label}</span>
                    <span style={{ fontSize: 11, color: "#98A1AB", marginLeft: 8 }}>{check.description}</span>
                  </div>
                  <span style={{ fontSize: 11, color: "#98A1AB", flexShrink: 0 }}>{check.points} pts</span>
                </label>
              );
            })}
          </div>
        )}
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "#98A1AB", textAlign: "center" }}>
          *Not all checks are needed for the average API
        </p>
      </div>

      {/* Advanced: headers + body (Pro only) */}
      {isPro && (
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            style={{ fontSize: 13, color: "#98A1AB", background: "none", border: "none", cursor: "pointer", marginBottom: 8 }}
          >
            {showAdvanced ? "▼" : "▶"} Advanced (headers{method === "POST" ? ", body" : ""})
          </button>
          {showAdvanced && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea
                value={headersRaw}
                onChange={(e) => setHeadersRaw(e.target.value)}
                placeholder={"Authorization: Bearer token\nContent-Type: application/json"}
                rows={3}
                className="input-dark"
                style={{ resize: "vertical", fontFamily: "monospace", fontSize: 13 }}
              />
              {method === "POST" && (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder='{"key": "value"}'
                  rows={4}
                  className="input-dark"
                  style={{ resize: "vertical", fontFamily: "monospace", fontSize: 13 }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {!isSignedIn && (
        <p style={{ fontSize: 12, color: "#98A1AB", marginTop: 8, textAlign: "center" }}>
          No account required · 3 free probes ·{" "}
          <a href="/sign-up" style={{ color: "#FF2566" }}>Sign up</a> for more
        </p>
      )}
    </form>
  );
}
